import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Bookmark, Star, GitFork, Users, TrendingUp, Sparkles, Clock, Send, Award, MessageCircle } from "lucide-react";

export default function CommunityProtocol() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forking, setForking] = useState(false);
  const [ratingStars, setRatingStars] = useState(0);
  const [ratingComment, setRatingComment] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/community/protocols/${id}`);
      setP(r.data);
      setRatingStars(r.data.my_rating || 0);
    } catch {
      toast.error("Could not load protocol.");
      nav("/community");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/community/protocols/${id}/save`);
      // Optimistically flip the label so the UI updates even if the follow-up GET is slow.
      setP((prev) => prev ? { ...prev, my_save: true, saves_count: (prev.saves_count || 0) + (r.data.already_saved ? 0 : 1) } : prev);
      if (r.data.already_saved) {
        toast.message("Already saved to your library.");
      } else {
        toast.success("Saved to your protocols with attribution.");
      }
      await load();
    } catch {
      toast.error("Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const submitRating = async (stars) => {
    if (!stars) return;
    try {
      await api.post(`/community/protocols/${id}/rate`, { stars, comment: ratingComment });
      toast.success("Rating submitted");
      setRatingStars(stars);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not rate.");
    }
  };

  const fork = async () => {
    // Forking = saving to my library first (creates a local copy), then user can edit & republish.
    setForking(true);
    try {
      const r = await api.post(`/community/protocols/${id}/save`);
      const localId = r.data.local_protocol_id;
      toast.success("Forked! Edit it in your Protocols library, then 'Share' to publish your improved version.");
      nav(`/protocols?focus=${localId}`);
    } catch {
      toast.error("Could not fork.");
    } finally {
      setForking(false);
    }
  };

  if (loading || !p) return <Layout><div className="text-ink-500" data-testid="detail-loading">Loading…</div></Layout>;

  return (
    <Layout>
      <Link to="/community" className="inline-flex items-center gap-2 text-sm text-ink-700 hover:text-ink-900 mb-6" data-testid="back-to-community">
        <ArrowLeft className="w-4 h-4" /> Back to community
      </Link>

      <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8 mb-6" data-testid="protocol-detail">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {p.is_featured && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 inline-flex items-center gap-1" data-testid="featured-badge">
                  <Award className="w-3 h-3" /> Featured
                </span>
              )}
              {p.lineage_depth > 0 && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-terracotta-50 text-terracotta-700 border border-terracotta-100 inline-flex items-center gap-1">
                  <GitFork className="w-3 h-3" /> Fork · depth {p.lineage_depth}
                </span>
              )}
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-ink-900" data-testid="protocol-name">{p.name}</h1>
            <div className="mt-3 text-sm text-ink-700 inline-flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span data-testid="protocol-author">By {p.author_display_name}</span>
              <span className="text-ink-400">·</span>
              <span>{new Date(p.published_at).toLocaleDateString()}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={save}
              disabled={saving}
              data-testid="save-protocol-btn"
              className="min-h-[44px] px-5 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Bookmark className="w-4 h-4" /> {p.my_save ? "Saved" : saving ? "Saving…" : "Save to my protocols"}
            </button>
            <button
              onClick={fork}
              disabled={forking}
              data-testid="fork-protocol-btn"
              className="min-h-[44px] px-5 rounded-full border border-paper-200 hover:bg-paper-100 text-ink-900 font-medium inline-flex items-center gap-2 disabled:opacity-50"
            >
              <GitFork className="w-4 h-4" /> {forking ? "Forking…" : "Fork & modify"}
            </button>
          </div>
        </div>

        {p.description && <p className="text-ink-700 mb-5">{p.description}</p>}

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
          <BigStat label="Saves" value={p.saves_count || 0} icon={<Bookmark className="w-4 h-4" />} testid="stat-saves" />
          <BigStat label="Uses" value={p.uses_count || 0} icon={<TrendingUp className="w-4 h-4" />} testid="stat-uses" />
          <BigStat label="Improved" value={p.improvement_pct != null ? `${p.improvement_pct}%` : "—"} icon={<Sparkles className="w-4 h-4" />} testid="stat-improvement" />
          <BigStat label="Avg Δ pain" value={p.avg_vas_delta != null ? p.avg_vas_delta.toFixed(2) : "—"} icon={<TrendingUp className="w-4 h-4" />} testid="stat-vas" />
          <BigStat label="Rating" value={p.avg_rating != null ? `${p.avg_rating.toFixed(1)}/5` : "—"} icon={<Star className="w-4 h-4" />} testid="stat-rating" />
        </div>

        {/* Tags */}
        {(p.complaint_tags?.length > 0 || p.focus_muscles?.length > 0 || p.modalities?.length > 0) && (
          <div className="space-y-2 mb-5">
            {p.complaint_tags?.length > 0 && (
              <Row label="Complaints">{p.complaint_tags.map((t) => <Tag key={t} text={t} tone="terracotta" />)}</Row>
            )}
            {p.focus_muscles?.length > 0 && (
              <Row label="Focus muscles">{p.focus_muscles.map((m) => <Tag key={m} text={m} tone="sage" />)}</Row>
            )}
            {p.modalities?.length > 0 && (
              <Row label="Modalities">{p.modalities.map((m) => <Tag key={m} text={m} tone="amber" />)}</Row>
            )}
          </div>
        )}

        {/* Steps */}
        <div className="mb-2 label-overline">Treatment sequence</div>
        <div className="space-y-2 mb-5" data-testid="protocol-steps">
          {(p.steps || []).map((s, i) => (
            <div key={`${s.technique || "step"}-${i}`} className="bg-paper-50 rounded-xl p-3 border border-paper-200 flex items-start gap-3">
              <span className="w-7 h-7 rounded-full bg-sage-500 text-white text-xs flex items-center justify-center shrink-0">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-ink-900">{s.technique}</div>
                <div className="text-xs text-ink-700 flex items-center gap-2 flex-wrap mt-0.5">
                  {s.target && <span>{s.target}</span>}
                  {s.duration_minutes && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {s.duration_minutes}m</span>}
                </div>
                {s.notes && <div className="text-sm text-ink-700 mt-1">{s.notes}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lineage */}
      {(p.lineage?.length > 0 || p.forks?.length > 0) && (
        <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8 mb-6">
          <div className="label-overline mb-3 inline-flex items-center gap-2"><GitFork className="w-4 h-4" /> Protocol lineage</div>
          {p.lineage?.length > 0 && (
            <div className="mb-4">
              <div className="text-xs text-ink-500 mb-2">Ancestors (oldest first)</div>
              <div className="flex items-center gap-2 flex-wrap" data-testid="lineage-ancestors">
                {p.lineage.map((a) => (
                  <Link key={a.id} to={`/community/protocols/${a.id}`} className="text-sm px-3 py-1.5 rounded-full bg-paper-50 hover:bg-paper-100 border border-paper-200 text-ink-700">
                    {a.name} <span className="text-ink-400">· {a.author_display_name}</span>
                  </Link>
                ))}
                <span className="text-ink-400">→</span>
                <span className="text-sm px-3 py-1.5 rounded-full bg-ink-900 text-white">{p.name} (this)</span>
              </div>
            </div>
          )}
          {p.forks?.length > 0 && (
            <div>
              <div className="text-xs text-ink-500 mb-2">Forks of this protocol</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="lineage-forks">
                {p.forks.map((f) => (
                  <Link key={f.id} to={`/community/protocols/${f.id}`} className="text-sm px-4 py-2 rounded-xl bg-paper-50 hover:bg-paper-100 border border-paper-200 text-ink-900">
                    <div className="font-medium">{f.name}</div>
                    <div className="text-xs text-ink-500">By {f.author_display_name} · {f.saves_count || 0} saves · {f.uses_count || 0} uses</div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Rating */}
      <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8 mb-6">
        <div className="label-overline mb-2">Rate this protocol</div>
        <p className="text-sm text-ink-700 mb-4">Help others find what works. Your rating updates the protocol's community score.</p>
        <div className="flex items-center gap-1 mb-4" data-testid="rating-stars">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              onClick={() => submitRating(n)}
              data-testid={`star-${n}`}
              className="w-10 h-10 rounded-full hover:bg-paper-100 inline-flex items-center justify-center"
            >
              <Star className={`w-5 h-5 ${n <= ratingStars ? "fill-amber-400 text-amber-400" : "text-ink-300"}`} />
            </button>
          ))}
          {ratingStars > 0 && <span className="ml-2 text-sm text-ink-700">You rated {ratingStars}/5</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            value={ratingComment}
            onChange={(e) => setRatingComment(e.target.value)}
            placeholder="Optional comment (visible to community)"
            className="flex-1 min-w-[220px] min-h-[40px] px-4 rounded-full bg-paper-50 border border-paper-200 text-sm outline-none"
            data-testid="rating-comment-input"
          />
          <button
            onClick={() => submitRating(ratingStars || 5)}
            disabled={!ratingStars}
            className="min-h-[40px] px-5 rounded-full bg-ink-900 text-white text-sm font-medium hover:bg-ink-700 disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="submit-rating-btn"
          >
            <Send className="w-4 h-4" /> Submit
          </button>
        </div>

        {p.recent_comments?.length > 0 && (
          <div className="mt-6 space-y-3">
            <div className="text-xs text-ink-500 inline-flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5" /> Recent comments</div>
            {p.recent_comments.map((c) => (
              <div key={c.id} className="bg-paper-50 rounded-xl p-3 border border-paper-200">
                <div className="flex items-center gap-2 mb-1 text-xs text-ink-500">
                  <span className="font-medium text-ink-900">{c.author_display_name}</span>
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-3 h-3 ${n <= c.stars ? "fill-amber-400 text-amber-400" : "text-ink-300"}`} />
                    ))}
                  </div>
                </div>
                <div className="text-sm text-ink-700">{c.comment}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function BigStat({ label, value, icon, testid }) {
  return (
    <div className="bg-paper-50 border border-paper-200 rounded-2xl p-3" data-testid={testid}>
      <div className="text-xs text-ink-500 inline-flex items-center gap-1.5">{icon}{label}</div>
      <div className="font-heading text-2xl text-ink-900 mt-1">{value}</div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="text-xs text-ink-500 w-28 shrink-0 pt-1.5 uppercase tracking-wider">{label}</div>
      <div className="flex-1 flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function Tag({ text, tone }) {
  const map = {
    terracotta: "bg-terracotta-50 text-terracotta-700 border-terracotta-100",
    sage: "bg-sage-50 text-sage-700 border-sage-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
  };
  return (
    <span className={`text-xs px-2.5 py-1 rounded-full border ${map[tone] || ""}`}>{text}</span>
  );
}
